// @givmo/passkeys — the account reset's screens on the web (AS-WEB13; SEC2-AS's P2: a way out for an
// account whose only factor is a passkey, and for one that lost its authenticator app).
//
// The CONTENT of a dialog, never a dialog of its own: it renders inside the "Confirm it's you" sheet
// when a lost link opens it there (a host's `renderLost`, `@givmo/step-up`), and inside the Sign-in &
// security card's own dialog for its "Can't use a passkey?". Either way it draws its own title.
//
// THE STEPS (the apps' reset, step for step — iOS `FactorResetSteps.swift`):
//   1. REQUEST: what happens, in short paragraphs (the 24-hour wait and why; coming back to finish);
//      the account's own address, shown, never typed (a signed-in person resets their own account,
//      and the code only ever goes to its inbox); "Email me a code", or "I already have a code".
//   2. VERIFY: the emailed code. The first proof ARMS the reset; one at or after the deadline
//      FINISHES it. "Email me a new code" asks again: every proof spends a fresh code.
//   3. OUTCOME: "Reset started", with the server's deadline and where to cancel; or the finished
//      reset. "Done" closes.
// FINISHING (AS-WEB17): opened while the account's reset is armed and its wait has run (the read's
// `pending_factor_reset.executes_at` is past), step 1 is "Finish the reset" — the code that finishes
// it — never the request's "There's a 24-hour wait"; and a code the server answers "not yet" (its
// clock, not ours) says when it can be finished, in decision 3's words. ⚠ "Not yet" is only for the
// reset the flow opened on: a pending answer with a LATER deadline is a new reset (that one was
// cancelled elsewhere since the read, and this proof armed another), so it reads "Reset started".
// Back (step 1) returns to whatever opened it; without one, the step offers Cancel. Opened by a lost
// link whose act names what the lost factor means for it (`StepUpLostLines.line`, AS-WEB18: Board's
// signing), step 1 says that first.
//
// ⚠ FOCUS FOLLOWS THE STEP (`step-focus.ts`): each step after the first focuses its title, the code
// step its first box, and a refused code puts focus back there.
//
// ⚠ NOTHING HERE COUNTS THE WAIT. The deadline is the server's (`executes_at`), printed and never
// counted down: a countdown would imply the wait is ours to shorten. After any proof the account's
// reads are re-read, so the card's notice and its passkeys follow at once.
// ⚠ A FINISHED RESET THAT TAKES AN AUTHENTICATOR OFF SIGNS THE CONSOLE OUT (AS-BE63). The backend then
// ends every session of the account (`revoke_sessions`, as Identity Platform ends every Firebase one),
// the suite's included. So that finish never re-reads: the read would answer 401 and the card would go
// on showing what the account no longer holds; the card takes the reset's own answer instead.
// Leaving the finish — its Done, or the sheet's own dismissal — signs out through the host's own
// `signOut` (which also releases the suite session; `sign-out-after-removal.ts`), and the person signs
// in again — without a code, as its words say. An account that held no authenticator (a passkey-only
// reset) loses no session: its finish says "You can set up a new passkey now" (D-AS-35 #15), so it
// re-reads and stays signed in. Without a `signOut` (a host that hands none) the old re-read stays.

import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, ErrorMessage, OtpField, cn, testIdProp } from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import { invalidateAccountFactors, recordFactorsRemoved } from './account-factors';
import { keepNumbersWhole } from './keep-numbers-whole';
import { useSignOutAfterRemoval, type RemovalSignOut } from './sign-out-after-removal';
import { useStepTitleFocus } from './step-focus';
import {
  FACTOR_RESET_STEP_WORDS as WORDS,
  factorResetWords,
  type FactorResetKind,
} from './factor-reset-copy';
import {
  requestFactorReset,
  verifyFactorReset,
  type FactorResetStatus,
} from './factor-reset-transport';

const CODE_LENGTH = 6;
const PREFIX = 'factor-reset';

const TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong outline-none';
const LINE = 'font-sans text-s14 font-w400 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:opacity-60';

export interface FactorResetFlowProps {
  /** Which reset: the passkey door's, or "Lost your authenticator?"'s two-factor reset. */
  kind: FactorResetKind;
  /** The signed-in account's own address: where the code goes. */
  email: string;
  /** Whether the account holds an authenticator app (`mfa.totp_enrolled`); `undefined` unknown. */
  alsoTurnsOffTwoFactor: boolean | undefined;
  /** Whether this account can set an authenticator app up again on the web. */
  authenticatorOffered: boolean;
  /**
   * Whether the account holds a passkey (`GET /users/me/passkeys`); `undefined` while unknown. The
   * two-factor reset says it removes passkeys too only where the account may hold one (AS-WEB18).
   */
  holdsPasskey?: boolean | undefined;
  /** The act's own line for this view (`StepUpLostLines.line`), said first on step 1. */
  contextLine?: string | undefined;
  /**
   * The armed reset's deadline, when the account has one (`mfa.pending_factor_reset.executes_at`, as
   * the flow opens). Past it, the flow opens on the finish (AS-WEB17).
   */
  pendingReset?: Date | null | undefined;
  /**
   * The signed-in account's uid: a finished reset's answer is written into its reads (AS-BE63), which
   * can no longer be made. Absent, nothing is written.
   */
  uid?: string | null | undefined;
  /**
   * The host's own sign-out (`useAuth().signOut`, which also releases the suite session): a finished
   * reset that takes an authenticator off ends every session of the account (AS-BE63), so leaving its
   * finish signs this console out.
   */
  signOut?: RemovalSignOut | undefined;
  /** Back to what opened it (the sheet); absent, the first step offers Cancel. */
  onBack?: () => void;
  /** Close: the outcome's Done, Cancel, and the sheet's own dismissal. */
  onClose: () => void;
}

type Step =
  | { readonly kind: 'request' }
  | { readonly kind: 'verify' }
  | { readonly kind: 'outcome'; readonly outcome: FactorResetStatus };

/** Slack for one deadline as two answers print it (the security read's, the verify's). */
const SAME_DEADLINE_MS = 60_000;

/**
 * Whether a pending answer to a finishing code names a LATER reset than the one the flow opened on:
 * that one was cancelled since the read, and this proof armed another, with its own 24 hours. (From
 * the backend source, `verify_factor_reset` arming when none is armed; never driven on dev.)
 */
function armedAnew(openedOn: Date | null, answered: Date | null): boolean {
  return (
    openedOn !== null &&
    answered !== null &&
    answered.getTime() > openedOn.getTime() + SAME_DEADLINE_MS
  );
}

/** The words a failed call shows: a dark surface its own line, anything else the server's or ours. */
function messageFor(thrown: unknown): string {
  if (isGivmoError(thrown)) {
    if (thrown.status === 404) return WORDS.unavailable;
    if (thrown.message.length > 0) return thrown.message;
  }
  return 'That didn’t work. Try again.';
}

function TextLink({
  children,
  onClick,
  disabled,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(LINK, 'self-center')}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

/**
 * The account's own address, SHOWN: a signed-in person resets their own account, and the code only
 * ever goes to its inbox, so a field that looked editable would promise a choice there is not.
 */
function AccountAddress({ email, testId }: { email: string; testId: string }) {
  return (
    <div className="flex flex-col gap-8">
      <span className="font-sans text-s12 font-w600 tracking-givmo text-black">
        {WORDS.emailLabel}
      </span>
      <span
        className="break-all rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12 font-sans text-s14 font-w400 tracking-givmo text-text-strong"
        {...testIdProp(testId)}
      >
        {email}
      </span>
    </div>
  );
}

function Paragraphs({ lines, testId }: { lines: readonly string[]; testId: string }) {
  return (
    <div className="flex flex-col gap-10" {...testIdProp(testId)}>
      {lines.map((line, index) =>
        index === 0 ? (
          <Dialog.Description key={line} className={LINE}>
            {keepNumbersWhole(line)}
          </Dialog.Description>
        ) : (
          <p key={line} className={LINE}>
            {keepNumbersWhole(line)}
          </p>
        ),
      )}
    </div>
  );
}

export function FactorResetFlow({
  kind,
  email,
  alsoTurnsOffTwoFactor,
  authenticatorOffered,
  holdsPasskey,
  contextLine,
  pendingReset,
  uid,
  signOut,
  onBack,
  onClose,
}: FactorResetFlowProps) {
  const queryClient = useQueryClient();
  // What the account held when the flow opened decides the words to the end: the re-read after a
  // finished reset reports the authenticator gone, and the finish must still say it went with it.
  const [held] = useState(() => ({ alsoTurnsOffTwoFactor, authenticatorOffered, holdsPasskey }));
  const words = factorResetWords(kind, held);
  // A reset whose wait has run is finished, not started again (AS-WEB17): fixed as the flow opens,
  // with the deadline it opened on (a re-read after a proof hands in the next reset's).
  const [opened] = useState(() =>
    pendingReset !== null && pendingReset !== undefined && pendingReset.getTime() <= Date.now()
      ? { finishing: true, deadline: pendingReset }
      : { finishing: false, deadline: null },
  );
  const finishing = opened.finishing;
  const [step, setStep] = useState<Step>({ kind: 'request' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'requesting' | 'verifying' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Focus follows the step (`step-focus.ts`): its title, or on the code step its first box — again
  // after a refused code, which clears the boxes.
  const title = useStepTitleFocus(step.kind);
  const codeBoxes = useRef<HTMLDivElement>(null);
  const focusCode = useCallback(() => {
    codeBoxes.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, []);
  useLayoutEffect(() => {
    if (step.kind === 'verify') focusCode();
  }, [step.kind, focusCode]);
  const { owe: oweSignOut, settle: settleSignOut } = useSignOutAfterRemoval(signOut);

  const askForCode = useCallback(async () => {
    if (busy !== null) return;
    setBusy('requesting');
    setError(null);
    try {
      await requestFactorReset(email);
      setCode('');
      setStep({ kind: 'verify' });
    } catch (thrown) {
      setError(messageFor(thrown));
    } finally {
      setBusy(null);
    }
  }, [busy, email]);

  const verify = useCallback(async () => {
    if (busy !== null || code.length !== CODE_LENGTH) return;
    setBusy('verifying');
    setError(null);
    try {
      const outcome = await verifyFactorReset(email, code);
      setStep({ kind: 'outcome', outcome });
      // The card's notice (armed) or its passkeys (finished) follow from the server's answer — but a
      // finished reset that took an authenticator off ended this session (AS-BE63): with a sign-out
      // to finish on, it is owed from here, and nothing is re-read (each read would answer 401); the
      // card behind the finish takes the reset's own answer instead (`recordFactorsRemoved`). The
      // words chose the same fact as the flow opened (`held`); unknown counts as held.
      const sessionEnded = outcome.status === 'completed' && held.alsoTurnsOffTwoFactor !== false;
      if (sessionEnded && signOut !== undefined) {
        oweSignOut();
        if (typeof uid === 'string' && uid.length > 0) {
          recordFactorsRemoved(queryClient, uid, { passkeysAndReset: true });
        }
      } else {
        void invalidateAccountFactors(queryClient);
      }
    } catch (thrown) {
      setError(messageFor(thrown));
      focusCode();
    } finally {
      // Each proof spends its code, success or failure: never send a spent one again.
      setCode('');
      setBusy(null);
    }
  }, [busy, code, email, queryClient, focusCode, signOut, oweSignOut, uid, held]);

  if (step.kind === 'outcome') {
    const finished = step.outcome.status === 'completed';
    const notYet =
      finishing &&
      step.outcome.status === 'pending' &&
      !armedAnew(opened.deadline, step.outcome.executesAt);
    return (
      <div
        className="flex flex-col gap-16"
        {...testIdProp(finished ? `${PREFIX}-completed` : `${PREFIX}-pending`)}
      >
        <Dialog.Title ref={title} tabIndex={-1} className={TITLE}>
          {finished ? words.completedTitle : notYet ? words.notYetTitle : WORDS.startedTitle}
        </Dialog.Title>
        <Paragraphs
          lines={
            step.outcome.status === 'completed'
              ? [words.completed]
              : notYet
                ? words.notYet(step.outcome.executesAt)
                : words.started(step.outcome.executesAt)
          }
          testId={`${PREFIX}-outcome-lines`}
        />
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          onClick={() => {
            onClose();
            settleSignOut();
          }}
          testId={`${PREFIX}-done`}
        >
          {WORDS.done}
        </Button>
      </div>
    );
  }

  if (step.kind === 'verify') {
    return (
      <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-verify-step`)}>
        <Dialog.Title className={TITLE}>{WORDS.verifyTitle}</Dialog.Title>
        <Paragraphs lines={[WORDS.verifyLine]} testId={`${PREFIX}-verify-line`} />
        <form
          className="flex flex-col gap-14"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
          {...testIdProp(`${PREFIX}-verify-form`)}
        >
          <AccountAddress email={email} testId={`${PREFIX}-verify-email`} />
          <div className="flex flex-col" ref={codeBoxes}>
            <OtpField
              length={CODE_LENGTH}
              value={code}
              onValueChange={(next) => {
                setCode(next);
                if (next.length > 0) setError(null);
              }}
              error={error !== null}
              aria-label={WORDS.codeLabel}
              testId={`${PREFIX}-code`}
            />
            {error !== null && <ErrorMessage testId={`${PREFIX}-error`}>{error}</ErrorMessage>}
          </div>
          <Button
            type="submit"
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={busy === 'verifying'}
            disabled={code.length !== CODE_LENGTH || busy === 'requesting'}
            testId={`${PREFIX}-verify`}
          >
            {WORDS.verify}
          </Button>
          <TextLink
            disabled={busy !== null}
            onClick={() => void askForCode()}
            testId={`${PREFIX}-resend`}
          >
            {WORDS.resend}
          </TextLink>
        </form>
        <div className="flex items-center">
          <TextLink
            disabled={busy !== null}
            onClick={() => {
              setError(null);
              setStep({ kind: 'request' });
            }}
            testId={`${PREFIX}-back`}
          >
            {WORDS.back}
          </TextLink>
        </div>
      </div>
    );
  }

  const opening = finishing ? words.finish : words.request;
  return (
    <div
      className="flex flex-col gap-16"
      {...testIdProp(finishing ? `${PREFIX}-finish-step` : `${PREFIX}-request-step`)}
    >
      <Dialog.Title ref={title} tabIndex={-1} className={TITLE}>
        {finishing ? words.finishTitle : words.title}
      </Dialog.Title>
      <Paragraphs
        lines={
          contextLine !== undefined && contextLine.length > 0 ? [contextLine, ...opening] : opening
        }
        testId={`${PREFIX}-request-lines`}
      />
      <div className="flex flex-col gap-14">
        <AccountAddress email={email} testId={`${PREFIX}-email`} />
        {error !== null && <ErrorMessage testId={`${PREFIX}-error`}>{error}</ErrorMessage>}
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading={busy === 'requesting'}
          onClick={() => void askForCode()}
          testId={`${PREFIX}-request`}
        >
          {WORDS.request}
        </Button>
        <TextLink
          disabled={busy !== null}
          onClick={() => {
            setError(null);
            setCode('');
            setStep({ kind: 'verify' });
          }}
          testId={`${PREFIX}-have-code`}
        >
          {WORDS.haveCode}
        </TextLink>
      </div>
      <div className="flex items-center">
        <TextLink
          disabled={busy !== null}
          onClick={onBack ?? onClose}
          testId={onBack !== undefined ? `${PREFIX}-back` : `${PREFIX}-cancel`}
        >
          {onBack !== undefined ? WORDS.back : WORDS.cancel}
        </TextLink>
      </div>
    </div>
  );
}
